import { useState } from "react";
import { useParams } from "react-router";
import { useNotify } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Eye, Loader2, Mail, Power } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  PageActions,
  PageBreadcrumb,
} from "@/components/atomic-crm/layout/PageActions";
import { PlatformGuard } from "@/modules/platform/PlatformGuard";
import {
  ModulesManager,
  type ModuleChange,
} from "@/modules/apps/ModulesManager";
import type { ModuleId } from "@/modules/registry";
import {
  resendCompanyAdminInvite,
  setCompanyDisabled,
  setCompanyModule,
  startViewAsCompany,
  usePlatformCompanyModules,
  usePlatformAuditLog,
  usePlatformCompany,
  viewCompanyAsOperator,
  type PlatformCompanySnapshot,
} from "@/modules/platform/platformApi";

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const ACTION_LABELS: Record<string, string> = {
  create_company: "Created company",
  disable_company: "Disabled company",
  reactivate_company: "Reactivated company",
  view_as_company: "Viewed company summary",
  start_view_as_company: "Opened as company",
  end_view_as_company: "Left company view",
  enable_module: "Turned on a module",
  disable_module: "Turned off a module",
  resend_admin_invite: "Resent admin invite",
};

/** One company: users, read-only "View as company", audit trail. */
export const PlatformCompanyPage = () => (
  <PlatformGuard>
    <CompanyDetail />
  </PlatformGuard>
);

const CompanyDetail = () => {
  const orgId = Number(useParams().id);
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { data: company, isPending, error } = usePlatformCompany(orgId);
  const { data: audit = [] } = usePlatformAuditLog(orgId);
  const [snapshot, setSnapshot] = useState<PlatformCompanySnapshot | null>(
    null,
  );
  const [busy, setBusy] = useState<
    "view" | "open" | "disable" | "invite" | null
  >(null);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["platform"] });

  const run = async (kind: typeof busy, action: () => Promise<void>) => {
    setBusy(kind);
    try {
      await action();
    } catch (e) {
      notify(e instanceof Error ? e.message : "Action failed", {
        type: "error",
      });
    } finally {
      setBusy(null);
    }
  };

  if (error) return <p className="text-sm text-destructive">{error.message}</p>;
  if (isPending) return <Skeleton className="h-48 w-full rounded-xl" />;
  if (!company) return <p className="text-sm">Company not found.</p>;

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      <PageActions>
        <PageBreadcrumb
          items={[
            { label: "Companies", to: "/platform" },
            { label: company.name },
          ]}
        />
      </PageActions>

      <div className="flex flex-wrap items-center gap-2">
        {company.is_home ? <Badge variant="secondary">Home</Badge> : null}
        {company.disabled_at ? (
          <Badge variant="muted">Disabled</Badge>
        ) : (
          <Badge variant="success">Active</Badge>
        )}
        <span className="text-sm text-muted-foreground">
          Created {formatDateTime(company.created_at)}
        </span>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy != null}
            onClick={() =>
              run("invite", async () => {
                const { sent_to } = await resendCompanyAdminInvite(company.id);
                notify(`Set-password email sent to ${sent_to}`, {
                  type: "success",
                });
                await refresh();
              })
            }
          >
            {busy === "invite" ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Mail className="size-4" />
            )}
            Resend admin invite
          </Button>
          {!company.is_home ? (
            <Button
              type="button"
              variant={company.disabled_at ? "outline" : "destructive"}
              size="sm"
              disabled={busy != null}
              onClick={() =>
                run("disable", async () => {
                  await setCompanyDisabled(company.id, !company.disabled_at);
                  await refresh();
                })
              }
            >
              {busy === "disable" ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Power className="size-4" />
              )}
              {company.disabled_at ? "Reactivate" : "Disable"}
            </Button>
          ) : null}
        </div>
      </div>

      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users">
            Users ({company.members.length})
          </TabsTrigger>
          <TabsTrigger value="modules">Modules</TabsTrigger>
          <TabsTrigger value="view">View as company</TabsTrigger>
          <TabsTrigger value="audit">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="overflow-hidden rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {company.members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium">
                      {[member.first_name, member.last_name]
                        .filter(Boolean)
                        .join(" ") || "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {member.email ?? "—"}
                    </TableCell>
                    <TableCell>
                      {member.administrator ? "Admin" : "Member"}
                    </TableCell>
                    <TableCell>
                      {member.disabled ? (
                        <Badge variant="muted">Disabled</Badge>
                      ) : (
                        <Badge variant="success">Active</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="modules">
          <CompanyModules orgId={company.id} onChanged={refresh} />
        </TabsContent>

        <TabsContent value="view" className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/40 px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Open Nomi as {company.name}</p>
              <p className="text-sm text-muted-foreground">
                See their dashboard, menu, modules and records exactly as they
                do. Read-only, recorded in Activity, ends after an hour or when
                you press Exit.
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              disabled={busy != null}
              onClick={() =>
                run("open", async () => {
                  await startViewAsCompany(company.id);
                  // Full reload so every screen reads as the company.
                  window.location.assign("/");
                })
              }
            >
              {busy === "open" ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Eye className="size-4" />
              )}
              Open as company
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <p className="flex-1 text-sm text-muted-foreground">
              Or a quick summary without leaving the console.
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy != null}
              onClick={() =>
                run("view", async () => {
                  setSnapshot(await viewCompanyAsOperator(company.id));
                  await refresh();
                })
              }
            >
              {busy === "view" ? (
                <Loader2 className="size-4 animate-spin" />
              ) : null}
              {snapshot ? "Refresh summary" : "Show summary"}
            </Button>
          </div>
          {snapshot ? <CompanySnapshot snapshot={snapshot} /> : null}
        </TabsContent>

        <TabsContent value="audit">
          {audit.length === 0 ? (
            <p className="text-sm text-muted-foreground">No activity yet.</p>
          ) : (
            <ul className="divide-y rounded-lg border bg-card">
              {audit.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
                >
                  <span>{ACTION_LABELS[entry.action] ?? entry.action}</span>
                  <span className="text-muted-foreground">
                    {formatDateTime(entry.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const CompanyModules = ({
  orgId,
  onChanged,
}: {
  orgId: number;
  onChanged: () => Promise<unknown>;
}) => {
  const notify = useNotify();
  const { data: rows, isPending } = usePlatformCompanyModules(orgId);
  const [busyModuleId, setBusyModuleId] = useState<ModuleId | null>(null);

  const apply = async (changes: ModuleChange[]) => {
    if (!changes.length) return;
    setBusyModuleId(changes[changes.length - 1].moduleId);
    try {
      for (const change of changes) {
        await setCompanyModule(
          orgId,
          change.moduleId,
          change.enabled,
          change.options,
        );
      }
      await onChanged();
    } catch (e) {
      notify(e instanceof Error ? e.message : "Could not update the module", {
        type: "error",
      });
    } finally {
      setBusyModuleId(null);
    }
  };

  if (isPending) return <Skeleton className="h-48 w-full rounded-xl" />;
  return (
    <ModulesManager
      rows={rows ?? []}
      busyModuleId={busyModuleId}
      onChange={(changes) => void apply(changes)}
    />
  );
};

const CompanySnapshot = ({
  snapshot,
}: {
  snapshot: PlatformCompanySnapshot;
}) => {
  const sections: Array<{ label: string; rows: string[] }> = [
    {
      label: "Recent accounts",
      rows: snapshot.recent_companies.map((row) => row.name),
    },
    {
      label: "Recent contacts",
      rows: snapshot.recent_contacts.map((row) =>
        [row.name, row.title].filter(Boolean).join(" · "),
      ),
    },
    {
      label: "Recent projects",
      rows: snapshot.recent_deals.map((row) =>
        [row.name, row.stage].filter(Boolean).join(" · "),
      ),
    },
    {
      label: "Recent tickets",
      rows: snapshot.recent_tickets.map((row) =>
        [row.subject, row.status].filter(Boolean).join(" · "),
      ),
    },
    {
      label: "Recent tasks",
      rows: snapshot.recent_tasks.map((row) => row.text),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {Object.entries(snapshot.counts).map(([key, value]) => (
          <div key={key} className="rounded-lg border bg-card px-3 py-2">
            <p className="text-xs capitalize text-muted-foreground">{key}</p>
            <p className="text-lg font-semibold tabular-nums">{value}</p>
          </div>
        ))}
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {sections.map((section) => (
          <div key={section.label} className="rounded-lg border bg-card p-3">
            <p className="mb-2 text-xs font-medium text-muted-foreground">
              {section.label}
            </p>
            {section.rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">None</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {section.rows.map((row, index) => (
                  <li key={`${section.label}-${index}`} className="truncate">
                    {row}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
